<template>
  <vue-draggable-resizable
    ref="textArea"
    class-name="crop-area"
    :style="visibleStyle"
    :min-width="1"
    :min-height="1"
    :x="x"
    :y="y"
    :w="containerWidth"
    :h="textSizes.height"
    :handles="[]"
    :parent="dragModeText"
    @dragstop="updatePlacement"
  >
    <span class="edit-text">{{ visibleText }}</span>
  </vue-draggable-resizable>
</template>

<script>
import VueDraggableResizable from "vue-draggable-resizable";
import { getStringSizes } from "@/utils/text.js";

// @group Areas
// Draggable text container
export default {
  name: "TextArea",
  components: {
    VueDraggableResizable
  },
  props: {
    // Coords of left-top text's corner
    placement: {
      type: Object,
      required: true,
      validator(val) {
        const { x, y } = val;
        return Number.isFinite(x) && Number.isFinite(y);
      }
    },
    dragModeText: {
      type: Boolean,
      default: true
    },
    // Text string
    text: {
      type: String,
      default: "Your Text"
    },
    // Text font family
    font: {
      type: String,
      default: "Arial"
    },
    // Text size
    size: {
      type: Number,
      default: 18
    },
    // Text color
    color: {
      type: String,
      default: "#ffffff"
    }
  },
  data() {
    return {
      x: this.placement.x,
      y: this.placement.y,
      visibleText: this.text,
      visibleStyle: {
        fontSize: this.size + "px",
        fontFamily: this.font,
        color: this.color
      },
    };
  },
  computed: {
    // @vuese
    // Text width in pixels
    textSizes() {
      return getStringSizes(this.text, this.font, this.size);
    },
    // @vuese
    // Text container with with padding and bounded by parent width
    containerWidth() {
      const maxWidth = this.$refs.textArea?.$el?.parentElement?.clientWidth;
      if (!maxWidth) {
        return this.textSizes.width + 2;
      }
      return Math.min(this.textSizes.width + 2, maxWidth);
    }
  },
  watch: {
    // @vuese
    // Update area placement when text is changed
    text() {
      this.fitText();
      this.setVisibleText(this.text);
    },
    // @vuese
    // Update area placement when font family is changed
    font() {
      this.fitText();
      this.setVisibleFont(this.font);
    },
    // @vuese
    // Update area placement when text size is changed
    size() {
      this.fitText();
      this.setVisibleSize(this.size);
    },
    // @vuese
    // Update text color
    color() {
      this.visibleStyle.color = this.color;
    },
    placement() {
      this.x = this.placement.x;
      this.y = this.placement.y;
    }
  },
  mounted() {
    /** init text area */
    this.fitText();
    this.setVisibleText(this.text);
    this.setVisibleFont(this.font);
    this.setVisibleSize(this.size);
    this.visibleStyle.color = this.color;
  },
  methods: {
    // @vuese
    // Update visible text with accounting of borders
    fitText() {
      const availableWidth = this.$refs.textArea.$el.parentElement.clientWidth;
      const availableHeight = this.$refs.textArea.$el.parentElement
        .clientHeight;
      const newWidth = this.textSizes.width;
      const newHeight = this.textSizes.height;

      if (newWidth > availableWidth) {
        this.updatePlacement(0, this.y);
        this.$emit("textOverlapped");
        return;
      } else if (newWidth + this.placement.x + 2 > availableWidth) {
        // Small padding of 2 pixels to prevent accidential ellipsing in some cases
        this.updatePlacement(availableWidth - newWidth - 2, this.y);
        this.$emit("textFit");
      } else {
        this.$emit("textFit");
      }

      if (newHeight > availableHeight) {
        this.updatePlacement(this.x, 0);
        this.$emit("textOverlapped");
      } else if (newHeight + this.placement.y > availableHeight) {
        this.updatePlacement(this.x, availableHeight - newHeight);
        this.$emit("textFit");
      } else {
        this.$emit("textFit");
      }
    },
    // @vuese
    // Change placement data to actual new
    // @arg x - left padding (in px) <br>
    // @arg y - top padding (in px)
    updatePlacement(x, y) {
      this.x = x;
      this.y = y;
      this.$emit("update:placement", {
        x: x,
        y: y
      });
    },
    // @vuese
    // Set visible text after rendering to wait change of width
    // @arg text - new text
    setVisibleText(text) {
      this.$nextTick(() => {
        this.visibleText = text;
      });
    },
    // @vuese
    // Set visible font family after rendering to wait change of width
    // @arg size - new font family
    setVisibleFont(font) {
      this.$nextTick(() => {
        this.visibleStyle.fontFamily = font;
      });
    },
    // @vuese
    // Set visible font size after rendering to wait change of width
    // @arg size - new font size
    setVisibleSize(size) {
      this.$nextTick(() => {
        this.visibleStyle.fontSize = size + "px";
      });
    }
  }
};
</script>

<style scoped>
.crop-area {
  /* Absolute positioned area will be place over the image */
  position: absolute;
  top: 0;
  left: 0;

  text-align: left;
  cursor: grab;

  overflow: hidden;
  text-overflow: ellipsis;
}
.crop-area:active {
  cursor: grabbing;
}
.edit-text {
  /** no wrap to use dots ellipsis (...) */
  white-space: nowrap;
  word-break: keep-all;

  /* disable select effect */
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}
</style>
